/**
 * Model attribution ("モデル出典").
 *
 * CC BY 4.0 requires the notice to travel with the *distributed artifact*, not
 * just with the repository. A deployed web page and a shipped desktop app are
 * both distribution, so this panel is a functional requirement of the demo, not
 * documentation. It is shared by both shells.
 *
 * The bytes live in `demo/fixtures/`, which the web shell serves as its Vite
 * `publicDir` — so `fixturePath` is a plain file name at the site root. Anything
 * whose rights are unrecorded must never appear in this table, and every entry
 * here must have a matching section in `demo/fixtures/NOTICE.md` (third-party)
 * or a row in `demo/fixtures/README.md` (project-authored).
 */

export interface ModelAttribution {
  readonly id: string;
  /** Label shown in the fixture picker. */
  readonly label: string;
  /**
   * Path of the bundled fixture relative to the site root, or `null` while the
   * fixture is not yet bundled. Never point this at a non-redistributable file.
   */
  readonly fixturePath: string | null;
  readonly approxBytes: number | null;
  readonly title: string;
  readonly creator: string;
  readonly sourceUrl: string | null;
  readonly licenseName: string;
  readonly licenseUrl: string | null;
  /** Required by CC BY: state whether the work was modified. */
  readonly changeStatement: string;
  /** True when the license obliges us to show the notice in the running UI. */
  readonly noticeRequired: boolean;
}

/**
 * Fixture rights mirror `demo/fixtures/NOTICE.md`, which in turn was assembled
 * from `crates/ifc-bim-json/fixtures/NOTICE.md` (plus its `.manifest.json`
 * sidecar) and `fixtures/ifc/samples/NOTICE.md` in the ifc-services repo, whose
 * `rights_basis: "redistributable_license"` findings live in
 * `fixtures/golden/parse/*.json`. Non-redistributable corpora (BLCJ, private
 * measured models) are absent by policy, not by omission.
 *
 * Ordered smallest-first: the first entries are the ones to reach for when
 * checking whether anything renders at all.
 */
export const MODEL_ATTRIBUTIONS: readonly ModelAttribution[] = [
  {
    id: "axis-only-mixed",
    label: "Axis only + Body 壁 (selection/refusal demo)",
    fixturePath: "axis-only-mixed.ifc",
    approxBytes: 1571,
    title: "axis-only-mixed.ifc",
    creator: "ifc-engine-core authors",
    sourceUrl: null,
    licenseName: "MIT OR Apache-2.0",
    licenseUrl: null,
    changeStatement: "Authored in this repository for the staged-D B receiver gate.",
    noticeRequired: false,
  },
  {
    id: "minimal-wall",
    label: "最小の 1 枚壁 (minimal wall)",
    fixturePath: "minimal_wall_u1_2026-07.ifc",
    approxBytes: 1825,
    title: "minimal_wall_u1_2026-07.ifc",
    creator: "ifc-engine-core authors",
    sourceUrl: null,
    licenseName: "MIT OR Apache-2.0",
    licenseUrl: null,
    changeStatement: "Authored in this repository.",
    noticeRequired: false,
  },
  {
    id: "bsi-stair",
    // Measured: this file plans 0 chunks. It is a buildingSMART gherkin *rule*
    // fixture — an IfcStair aggregating an IfcStairFlight with no
    // IfcProductDefinitionShape anywhere — so there is nothing to draw and the
    // engine is right to say so. Kept because "valid IFC, nothing renderable"
    // is a case the demo should show honestly, but labelled so an empty canvas
    // is never mistaken for a broken viewer.
    label: "IFC4.3 階段 (buildingSMART, MIT) — 形状なし",
    fixturePath: "ifc43-bsi-gherkin-blt003-stair.ifc",
    approxBytes: 1432,
    title: "pass-blt003-IfcStair-aggregating-IfcStairFlight.ifc",
    creator: "buildingSMART International Ltd.",
    sourceUrl:
      "https://github.com/buildingSMART/ifc-gherkin-rules/blob/e2b27c1/test/files/BLT/blt003/pass-blt003-IfcStair-aggregating-IfcStairFlight.ifc",
    licenseName: "MIT",
    licenseUrl: null,
    changeStatement: "Unmodified copy; filename changed locally.",
    noticeRequired: true,
  },
  {
    id: "bsi-wall-opening-window",
    label: "開口・窓つき壁 (buildingSMART, CC BY 4.0)",
    fixturePath: "bsi-wall-with-opening-and-window.ifc",
    approxBytes: 12492,
    title: "wall-with-opening-and-window.ifc (IFC4, ReferenceView_V1.2)",
    creator: "© buildingSMART International Ltd.",
    sourceUrl:
      "https://github.com/buildingSMART/Sample-Test-Files/blob/705436a/IFC%204.0.2.1%20(IFC%204)/ISO%20Spec%20-%20ReferenceView_V1.2/wall-with-opening-and-window.ifc",
    licenseName: "Creative Commons Attribution 4.0 International (CC BY 4.0)",
    licenseUrl: "https://creativecommons.org/licenses/by/4.0/",
    changeStatement: "Unmodified copy; filename changed locally.",
    noticeRequired: true,
  },
  {
    id: "synthetic-tower",
    label: "合成タワー (synthetic tower, 1024 products / 8 storeys)",
    fixturePath: "synthetic-tower-1024p-8s.ifc",
    approxBytes: 199374,
    title: "synthetic-tower-1024p-8s.ifc (generated)",
    creator: "ifc-engine-core authors",
    sourceUrl: null,
    licenseName: "MIT OR Apache-2.0",
    licenseUrl: null,
    changeStatement:
      "Generated deterministically by examples/generate_synthetic_ifc.rs; committed so the demo builds without a Rust toolchain.",
    noticeRequired: false,
  },
  {
    id: "bsi-pcert-building",
    label: "IFC4.3 建築モデル (buildingSMART, CC BY 4.0)",
    fixturePath: "ifc43-bsi-pcert-building-architecture.ifc",
    approxBytes: 220789,
    title: "PCERT-Sample-Scene/Building-Architecture.ifc (IFC4X3_ADD2)",
    creator: "© buildingSMART International Ltd.",
    sourceUrl:
      "https://github.com/buildingSMART/Sample-Test-Files/blob/cecf656/IFC%204.3.2.0%20(IFC4X3_ADD2)/PCERT-Sample-Scene/Building-Architecture.ifc",
    licenseName: "Creative Commons Attribution 4.0 International (CC BY 4.0)",
    licenseUrl: "https://creativecommons.org/licenses/by/4.0/",
    changeStatement: "Unmodified copy; filename changed locally.",
    noticeRequired: true,
  },
  {
    id: "synthetic-office",
    label: "合成オフィスビル (synthetic office, 10,018 products / 16 storeys)",
    fixturePath: "synthetic-office-10018p-16s.ifc",
    approxBytes: 2435383,
    title: "synthetic-office-10018p-16s.ifc (generated)",
    creator: "ifc-engine-core authors",
    sourceUrl: null,
    licenseName: "MIT OR Apache-2.0",
    licenseUrl: null,
    changeStatement:
      "Generated deterministically by examples/generate_synthetic_ifc.rs --building; committed so the demo builds without a Rust toolchain.",
    noticeRequired: false,
  },
];

export function findAttribution(id: string): ModelAttribution | null {
  return MODEL_ATTRIBUTIONS.find((entry) => entry.id === id) ?? null;
}

function row(label: string, value: string): HTMLElement {
  const dt = document.createElement("dt");
  dt.textContent = label;
  const dd = document.createElement("dd");
  dd.textContent = value;
  const wrapper = document.createElement("div");
  wrapper.className = "attr-row";
  wrapper.append(dt, dd);
  return wrapper;
}

function linkRow(label: string, href: string, text: string): HTMLElement {
  const dt = document.createElement("dt");
  dt.textContent = label;
  const dd = document.createElement("dd");
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.textContent = text;
  anchor.rel = "noreferrer noopener";
  anchor.target = "_blank";
  dd.append(anchor);
  const wrapper = document.createElement("div");
  wrapper.className = "attr-row";
  wrapper.append(dt, dd);
  return wrapper;
}

/**
 * Render the attribution panel.
 *
 * `null` means a user-supplied file: we know nothing about its rights, so we say
 * exactly that rather than showing a stale notice from the previous model.
 */
export function renderAttributionPanel(
  container: HTMLElement,
  attribution: ModelAttribution | null,
): void {
  container.replaceChildren();

  const heading = document.createElement("h2");
  heading.textContent = "モデル出典";
  container.append(heading);

  if (attribution === null) {
    const note = document.createElement("p");
    note.className = "attr-empty";
    note.textContent =
      "読み込み中のモデルは利用者が選んだファイルです。出典・ライセンスは配布元の表示に従ってください。";
    container.append(note);
    return;
  }

  const list = document.createElement("dl");
  list.className = "attr-list";
  list.append(row("タイトル", attribution.title));
  list.append(row("作者・権利者", attribution.creator));
  list.append(row("ライセンス", attribution.licenseName));
  if (attribution.licenseUrl !== null) {
    list.append(linkRow("ライセンス全文", attribution.licenseUrl, attribution.licenseUrl));
  }
  if (attribution.sourceUrl !== null) {
    list.append(linkRow("入手元", attribution.sourceUrl, "source"));
  }
  list.append(row("改変の有無", attribution.changeStatement));
  container.append(list);

  if (attribution.noticeRequired) {
    const note = document.createElement("p");
    note.className = "attr-required";
    note.textContent = "この表示はライセンス上の義務です（配布物での表示要求）。";
    container.append(note);
  }
}
